import type { Dayjs } from 'dayjs';
import type { TranslationFunction } from 'tradera/lang/translate';
import { swedishTimeZoneDate } from 'tradera/utils/date';
/**
 * NOTE: there's a hook useFormatEndDate in src/hooks/use-format-end-date.ts
 * that will help out with dayjs and translation func
 */
export const formatEndDate = (
    endDateInput: string | Date,
    t: TranslationFunction,
    nowDateInput: string | Date,
    dayjs: () => Dayjs
): string => {
    if (!endDateInput || !nowDateInput) return '';

    const endDate = swedishTimeZoneDate(endDateInput, dayjs);
    const now = swedishTimeZoneDate(nowDateInput, dayjs);
    if (endDate.isSameOrAfter(now.add(1, 'week'), 'day')) {
        return endDate.format('D MMM HH:mm');
    }
    if (endDate.isSameOrAfter(now.add(2, 'day'), 'day')) {
        return endDate.format('ddd HH:mm');
    }
    if (endDate.isSameOrAfter(now.add(1, 'day'))) {
        return `${t('vip_tomorrow')} ${endDate.format('HH:mm')}`;
    }
    if (endDate.isSameOrAfter(now.add(1, 'minute'))) {
        const hours = endDate.diff(now, 'hour');
        const minutes = endDate.diff(now, 'minute') % 60;
        const timeParts = [];
        if (hours > 0) {
            timeParts.push(t('vip_hours', { count: hours }));
        }
        if (minutes > 0) {
            timeParts.push(t('vip_minutes', { count: minutes }));
        }
        return timeParts.join(' ');
    }
    if (endDate.isAfter(now)) {
        return t('vip_less_than_1_min_left');
    }
    return t('vip_ended');
};

export const formatNumberWithSeparators = (
    number: number | string,
    separator: string = '\u2006'
): string => {
    return number
        .toString()
        .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + separator);
};

// https://developer.mozilla.org/en/docs/Web/JavaScript/Reference/Global_Objects/Number/toLocaleString
function toLocaleStringSupportsLocales(): boolean {
    try {
        Number(0).toLocaleString('i');
    } catch (e) {
        return e instanceof RangeError;
    }
    return false;
}

// https://developer.mozilla.org/en/docs/Web/JavaScript/Reference/Global_Objects/Number/toLocaleString
function toLocaleStringSupportsOptions(): boolean {
    return !!(
        typeof Intl === 'object' &&
        Intl &&
        typeof Intl.NumberFormat === 'function'
    );
}

const isLocaleFormattingSupportedInBrowser: boolean =
    toLocaleStringSupportsLocales() && toLocaleStringSupportsOptions();

export const formatSellerDsrAverage = (
    value?: number | null,
    locale: string = 'sv-SE'
): string | undefined => {
    return value?.toLocaleString(locale, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
    });
};

type CurrencyOptions = Intl.NumberFormatOptions & {
    preferredCurrency?: string;
};

export const getCurrencySymbol = (
    locale: string,
    overrideOptions: CurrencyOptions = {}
): string => {
    const defaultOptions: Intl.NumberFormatOptions = {
        style: 'currency',
        currency: 'SEK',
        currencyDisplay:
            locale.startsWith('sv') &&
            (overrideOptions.currency || 'SEK') === 'SEK' &&
            (overrideOptions.preferredCurrency || 'SEK') === 'SEK'
                ? 'symbol'
                : 'code',
        useGrouping: false,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    };
    return (0)
        .toLocaleString(locale, {
            ...defaultOptions,
            ...overrideOptions
        } as Intl.NumberFormatOptions)
        .replace(/\d/g, '')
        .trim();
};

export const formatDateWithDayOfWeek = (
    dateString: string,
    locale: string
): string => {
    if (!isLocaleFormattingSupportedInBrowser) {
        return dateString;
    }

    const options: Intl.DateTimeFormatOptions = {
        weekday: 'long',
        year: undefined,
        month: 'long',
        day: 'numeric'
    };
    return new Intl.DateTimeFormat(locale, options).format(
        Date.parse(dateString)
    );
};

export const formatItemId = (itemId?: number): string | undefined => {
    if (!itemId || !Number.isInteger(itemId)) return undefined;

    return itemId.toLocaleString('sv-SE'); // Groupings of 3 separated by spaces
};

export const formatOrderNumber = (
    orderNr?: string | number
): string | undefined =>
    formatItemId(
        typeof orderNr === 'string' ? Number.parseInt(orderNr) : orderNr
    );
